import { AxiosPromise } from "axios";
import React, { useState } from "react";
import { EditSectionWithOnSuccess, EditSectionWithRedirect } from "./EditSectionTemplate";
import { useValidators } from "formvalidation/hooks";
import ValidatorContext from "formvalidation/FormContext";
import { SectionTemplate } from "./SectionTemplate";
import { Button, Message } from "semantic-ui-react";
import { EditNewSection, Section } from "./types";
import { ButtonPrimary, ButtonText } from "components/Atoms/Button";
import { MultiStepEditSectionStep, MultiStepEditSectionSteps } from "./MultiStepEditSectionSteps";

export type MultiStepEditSection<T> = {
  sectionSteps?: MultiStepEditSectionStep[]
  section: EditNewSection
  getPrevSection: () => T | null
  getNextSection: () => T | null
  isLastStep: () => boolean
}

export type MultiStepEditSectionTemplateProps<T extends string> = {
  submit: () => AxiosPromise<server.dto.ApiResponse | server.dto.ApiResponseWrapper<any>>;
  sections: { [K in T]: MultiStepEditSection<T> };
  initialSection: T;
} & (EditSectionWithRedirect | EditSectionWithOnSuccess)

export const MultiStepEditSectionTemplate = <T extends string>(props: MultiStepEditSectionTemplateProps<T>) => {
  const { submit, sections } = props;
  const [activeSection, setActiveSection] = useState<T>(props.initialSection);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const validators = useValidators();

  const onNext = async () => {
    if (isLoading) return;

    setIsLoading(true);
    const validatorResult = await validators.validateAll();

    if (validatorResult.hasErrors) {
      validatorResult.scrollToFirstError();
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setActiveSection(sections[activeSection].getNextSection());
  }

  const onSubmit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    const validatorResult = await validators.validateAll();

    if (validatorResult.hasErrors) {
      validatorResult.scrollToFirstError();
      setIsLoading(false);
      return;
    }

    const response = await submit();

    setServerErrors(response.data.errors.map(e => e.message));

    let itemId: number = undefined;
    let isSuccessful = false

    if (!response.data.hasErrors) {
      isSuccessful = true;
      if ("data" in response.data && typeof response.data.data === "number") {
        itemId = response.data.data;
      }
      if ("data" in response.data && (typeof response.data.data === "object" && "fileId" in response.data.data)) {
        itemId = response.data.data.fileId;
      }
    }

    if (isSuccessful) {
      if ("redirectUrl" in props) {
        window.location.href = props.redirectUrl(itemId);
      } else if ("onSuccess" in props) {
        props.onSuccess(itemId);
      }
    } else {
      setIsLoading(false);
    }
  }

  const NextBackFooter = () => <div key="nextBackFooter" className="flex-column gap">
    {serverErrors.length > 0 && <Message color="red">
      {serverErrors.map((error) => <div key={error}>{error}</div>)}
    </Message>}
    <div className="flex justify-space-between">
      {sections[activeSection].getPrevSection() ? <ButtonText
        onClick={() => setActiveSection(sections[activeSection].getPrevSection())}
        label={RESX.Buttons.btnPrevious}
      >
      </ButtonText> : <div />}
      <ButtonPrimary
        onClick={onNext}
        label={RESX.Buttons.btnNextStep}
        loading={isLoading}
        disabled={isLoading}
      />
    </div>
  </div>

  const SubmitFooter = () => <div key="submitFooter" className="flex-column gap">
    {serverErrors.length > 0 && <Message color="red">
      {serverErrors.map((error) => <div key={error}>{error}</div>)}
    </Message>}
    <div className="flex justify-space-between">
      {sections[activeSection].getPrevSection() ? <ButtonText
        onClick={() => setActiveSection(sections[activeSection].getPrevSection())}
        label={RESX.Buttons.btnPrevious}
      >
      </ButtonText> : <div />}
      <Button
        primary
        type="submit"
        loading={isLoading}
        disabled={isLoading}
      >
        {RESX.Buttons.btnSave}
      </Button>
    </div>
  </div>

  const mapActiveSection = (): Section => {
    const section = sections[activeSection].section;

    // If the section is custom, we cannot add anything to it
    if (section.type === "custom") {
      return section;
    }

    const steps = sections[activeSection].sectionSteps ? [{
      key: "steps",
      label: "",
      hideLabel: true,
      compact: true,
      render: () => <MultiStepEditSectionSteps
        steps={sections[activeSection].sectionSteps}
        onPrev={() => setActiveSection(sections[activeSection].getPrevSection())}
        onNext={onNext}
      />,
    }] : [];

    // For field sections, we can add a step field at the top
    return {
      ...section,
      fields: [...steps, ...section.fields.map((field) => ({
        ...field,
        inPageEdit: undefined,
        isEmpty: false,
        render: field.renderEditNew,
      }))],
    }
  }

  const mappedActiveSection = mapActiveSection();

  const displayNextStep = sections[activeSection].getNextSection() && !sections[activeSection].isLastStep();
  const displaySubmit = sections[activeSection].isLastStep();

  return <ValidatorContext settings={validators.validatorSettings}>
    <form onSubmit={e => {
      e.preventDefault();

      // Stop propagation because we can have a form in a form (e.g. form > modal > form)
      e.stopPropagation();
      onSubmit();
    }}>
      <SectionTemplate
        isInPageEditable={false}
        compact
        sections={[mappedActiveSection,
          {
            key: "nextBack",
            type: "custom",
            hidden: !displayNextStep,
            render: () => <NextBackFooter />
          },
          {
            key: "submit",
            type: "custom",
            hidden: !displaySubmit,
            render: () => <SubmitFooter />
          }
        ]}
      />
    </form>
  </ValidatorContext>
}